import { CONFIG } from "../constants.js";
import { ScoringManager } from "../managers/ScoringManager.js";
import { BackgroundManager } from "../managers/BackgroundManager.js";
import { UIManager } from "../managers/UIManager.js";
import { triggerShake } from "./ScreenShake.js";

/**
 * Sound options for an asteroid impact: stereo pan from the asteroid's horizontal position and
 * `heavy` once the bullet caliber upgrade is active (evaluated before the impact's points land).
 * @param {any} game
 * @param {any} asteroid
 * @returns {{ heavy: boolean, pan: number }}
 */
function soundOptions(game, asteroid) {
  const width = game && game.view && game.view.width;
  const cx =
    asteroid && typeof asteroid.x === "number" ? asteroid.x + (asteroid.width || 0) / 2 : 0;
  const pan = width > 0 ? Math.max(-1, Math.min(1, (cx / width) * 2 - 1)) : 0;
  return { heavy: ScoringManager.bulletUpgradeActive(game), pan };
}

/**
 * EventHandlers – centralized wiring of EventBus driven gameplay side‑effects.
 *
 * Purpose
 * -------
 * Provide one auditable location where score mutations, VFX (explosions / particles), UI popups
 * and terminal state transitions (game over) are bound to domain events. This keeps `game.js`
 * slim and makes it easy to reason about all non‑pure reactions the game performs each frame.
 *
 * Behavior & Covered Events
 * -------------------------
 * - bulletHitAsteroid: Awards score (different value for hardened asteroids), spawns an
 *   explosion entity, optionally a score popup, and plays the asteroid-hit or planet-explosion
 *   sound via the optional `game.audio` (AudioManager), panned by screen position and heavier
 *   once the bullet caliber upgrade is active.
 * - bulletDamagedAsteroid: Non-lethal hit on a hardened planet; plays the regular asteroid-hit
 *   sound (same pan / heavy rules).
 * - playerHitAsteroid: Plays the fatal crash sound, cuts the music, and triggers the game over
 *   sequence exactly once per emission chain.
 * - Planet kills and the player's death also request a short screen shake (render-only, skipped
 *   under prefers-reduced-motion; see systems/ScreenShake.js).
 * - collectedStar (score path): Awards score (bonus for red stars), plays the star or bonus-star
 *   pickup sound (panned by position) and shows a popup for red stars.
 * - collectedStar (particle path): Emits a deterministic radial particle burst whose velocity
 *   magnitudes incorporate RNG variance for visual variety while keeping angle distribution stable.
 *
 * Determinism
 * -----------
 * Deterministic outcomes rely on the caller providing a seeded RNG (`game.rng`). Angle placement of
 * the star particle burst is fixed; only speed variance is randomized, preserving reproducible tests
 * when the RNG seed is controlled.
 *
 * Performance
 * -----------
 * Handlers avoid heap allocations except when pushing pooled entities / particles. Loops are tight
 * and short due to bounded burst counts (`CONFIG.STAR.PARTICLE_BURST`). Popup option objects are
 * small literals created only for qualifying events (rare vs per‑frame). All other work defers to
 * existing pooled systems (`particlePool`, `createExplosion`).
 *
 * Side Effects
 * ------------
 * Mutates: `game.score`, `game.particles[]`, popup/UI layers, and may transition game state via
 * `game.gameOver()`. Calls rendering / audio hooks indirectly through `createExplosion` & popups.
 *
 * Failure Modes / Defensive Notes
 * -------------------------------
 * - Missing properties on payload objects are gracefully ignored (guards check existence).
 * - If popup helpers are absent, scoring still proceeds without throwing.
 * - Hardened asteroid handling defaults to a dedicated score constant.
 *
 * Testing
 * -------
 * The returned aggregated unsubscribe function enables isolated unit tests: register → emit events
 * → assert game state → teardown (unsubscribe) to prevent test cross‑pollination.
 */
export const EventHandlers = {
  /**
   * Register all event handlers against the supplied game instance.
   *
   * Contract
   * --------
   * Input: A mutable `game` object exposing: `events`, `score`, `rng`, pools (particlePool,
   * explosionPool), collections (particles), creation helpers (`createExplosion`,
   * `createScorePopup`), scoring updater (`updateScore`), and terminal method (`gameOver`).
   * Output: A single function that when invoked unsubscribes every registered handler exactly once.
   *
   * Ordering & Idempotency
   * ----------------------
   * Order of handler registration matches declaration order; no reliance on inter‑handler ordering
   * currently exists. Calling `register` multiple times without invoking prior unsubscribe will
   * duplicate listeners—callers should treat this as a one‑shot setup per game lifecycle.
   *
   * Error Handling
   * --------------
   * Handlers are resilient to partial payloads and will early return or branch safely when optional
   * helpers are missing (e.g., `createScorePopup`).
   *
   * @param {any} game Mutable game instance (loosely typed to avoid circular imports in JSDoc)
   * @returns {() => void} Unsubscribe all registered event listeners.
   */
  register(game) {
    const { events } = game;
    /** @type {Array<() => void>} */
    const unsubs = [];

    unsubs.push(
      // @ts-ignore
      /**
       * bulletHitAsteroid handler
       
       * Payload Shape: { asteroid: { x:number, y:number, width:number, height:number, isHardened?:boolean } }
       * Effects: Mutates score, triggers explosion entity creation, optional score popup (+100) when
       *           asteroid is hardened, then invokes score UI refresh.
       * Determinism: Explosion position derived from asteroid center (pure). Score constant selection
       *              is branch‑based (no randomness). Popup styling fixed.
       * Failure Modes: If asteroid missing, handler becomes a no‑op except safe `updateScore` call.
       * @param {any} payload
       */
      events.on("bulletHitAsteroid", function (/** @type {{ asteroid:any }} */ payload) {
        const { asteroid } = payload;
        const sound = soundOptions(game, asteroid);
        let add = CONFIG.GAME.ASTEROID_SCORE;
        if (asteroid && asteroid.isHardened) {
          add = asteroid.isBonus
            ? CONFIG.GAME.ASTEROID_SCORE_BONUS
            : CONFIG.GAME.ASTEROID_SCORE_HARDENED;
        }
        const r = ScoringManager.add(game, add);
        if (asteroid) {
          if (asteroid.isHardened) {
            game.hardenedAsteroidsKilled = (game.hardenedAsteroidsKilled || 0) + 1;
            if (asteroid.isBonus) {
              game.bonusAsteroidsKilled = (game.bonusAsteroidsKilled || 0) + 1;
            }
          } else {
            game.regularAsteroidsKilled = (game.regularAsteroidsKilled || 0) + 1;
          }
        }
        game.createExplosion(asteroid.x + asteroid.width / 2, asteroid.y + asteroid.height / 2);
        if (asteroid && asteroid.isHardened) {
          game.audio?.playHardenedExplosion(!!asteroid.isBonus, sound);
        } else {
          game.audio?.playAsteroidHit(sound);
        }
        if (asteroid && asteroid.isHardened) triggerShake(game, asteroid.isBonus ? 7 : 5, 0.32);
        if (asteroid && asteroid.isHardened && typeof game.createScorePopup === "function") {
          const baseColor = CONFIG.COLORS.SCORE.POPUP;
          const opts = {
            color: baseColor,
            fontSize: 20,
            fontWeight: "700",
            glow: true,
            glowColor: baseColor,
            glowBlur: 12,
            stroke: CONFIG.COLORS.SCORE.POPUP_STROKE,
            maxLife: 1.2,
          };
          game.createScorePopup(
            asteroid.x + asteroid.width / 2,
            asteroid.y + asteroid.height / 2,
            r && typeof r.base === "number" ? r.base : add,
            opts
          );
        }
        game.updateScore();
      })
    );

    unsubs.push(
      /**
       * bulletDamagedAsteroid handler – non-lethal hit on a hardened planet; plays the same impact
       * sound as a regular asteroid hit. Scoring and visuals are handled elsewhere.
       * @param {any} payload
       */
      events.on("bulletDamagedAsteroid", function (/** @type {{ asteroid:any }} */ payload) {
        game.audio?.playAsteroidHit(soundOptions(game, payload && payload.asteroid));
        // The hit counts toward the accuracy shown next to the score.
        if (typeof game.updateScore === "function") game.updateScore();
      })
    );

    unsubs.push(
      events.on("playerHitAsteroid", () => {
        if (game && /** @type {any} */ (game)._dramaticEndPlayed) {
          game.gameOver();
          return;
        }
        /** @type {any} */ (game)._dramaticEndPlayed = true;
        game.audio?.playFatal();
        game.audio?.setMusic("off");
        try {
          const p = /** @type {any} */ (game.player);
          if (p && typeof game.createExplosion === "function") {
            const cx = (p.x || 0) + (p.width || 0) / 2;
            const cy = (p.y || 0) + (p.height || 0) / 2;
            /** @param {number} min @param {number} max @returns {number} */
            game.createExplosion(cx, cy);
          }
          triggerShake(game, 12, 0.5);
        } catch {
          /* ignore explosion side-effects */
        }
        try {
          if (UIManager && typeof UIManager.showDramaticEnd === "function") {
            UIManager.showDramaticEnd();
          }
        } catch {
          /* optional UI flourish */
        }
        game.gameOver();
      })
    );

    unsubs.push(
      // @ts-ignore
      /**
       * collectedStar (score branch)
       *
       * Payload Shape: { star: { x:number, y:number, width:number, height:number, isRed?:boolean } }
       * Effects: Adds base or red‑bonus score, updates UI score, optionally spawns +50 popup (red only).
       * Determinism: Score increment is branch‑based; popup styling fixed.
       * Failure Modes: Missing star payload yields guarded no‑op aside from safe UI refresh attempt.
       * @param {any} payload
       */
      events.on("collectedStar", function (/** @type {{ star:any }} */ payload) {
        const { star } = payload;
        const add = star && star.isRed ? CONFIG.GAME.STAR_SCORE_BONUS : CONFIG.GAME.STAR_SCORE;
        game.audio?.playStarCollect({
          bonus: !!(star && star.isRed),
          pan: soundOptions(game, star).pan,
        });
        const r = ScoringManager.add(game, add);
        try {
          game.regularStarsCollected = (game.regularStarsCollected || 0) + 1;
          if (star && star.isRed) {
            game.bonusStarsCollected = (game.bonusStarsCollected || 0) + 1;
          }
        } catch {
          /* stat increments optional */
        }
        game.updateScore();
        if (star && star.isRed && typeof game.createScorePopup === "function") {
          const baseColor = CONFIG.COLORS.SCORE.POPUP;
          const opts = {
            color: baseColor,
            fontSize: 20,
            fontWeight: "700",
            glow: true,
            glowColor: baseColor,
            glowBlur: 12,
            stroke: CONFIG.COLORS.SCORE.POPUP_STROKE,
            maxLife: 1.2,
          };
          game.createScorePopup(
            star.x + star.width / 2,
            star.y + star.height / 2,
            r && typeof r.base === "number" ? r.base : add,
            opts
          );
        }
      })
    );

    unsubs.push(
      // @ts-ignore
      /**
       * collectedStar (particle burst branch)
       *
       * Payload Shape: same as score branch.
       * Effects: Allocates (via pool) N radial particles whose base angles are evenly distributed.
       * Performance: O(N) where N = CONFIG.STAR.PARTICLE_BURST (small constant). Single push per particle.
       * Determinism: Angle distribution stable; speed variance depends on seeded RNG for reproducibility.
       * Failure Modes: Missing star payload short‑circuits loop. Pool acquire errors propagate (none expected).
       * @param {any} payload
       */
      events.on("collectedStar", function (/** @type {{ star:any }} */ payload) {
        const { star } = payload;
        const rng = game.rng;
        const particleMult =
          typeof game._performanceParticleMultiplier === "number"
            ? Math.max(0.1, Math.min(1, game._performanceParticleMultiplier))
            : 1;
        const burstCount = Math.max(1, Math.round(CONFIG.STAR.PARTICLE_BURST * particleMult));
        const budget = Number.isFinite(game._particleBudget)
          ? game._particleBudget
          : Number.POSITIVE_INFINITY;
        for (let p = 0; p < burstCount; p++) {
          if (game.particles.length >= budget) break;
          game.particles.push(
            game.particlePool.acquire(
              star.x + star.width / 2,
              star.y + star.height / 2,
              Math.cos((CONFIG.TWO_PI * p) / burstCount) *
                (rng.range(0, CONFIG.STAR.PARTICLE_BURST_SPEED_VAR) +
                  CONFIG.STAR.PARTICLE_BURST_SPEED_MIN),
              Math.sin((CONFIG.TWO_PI * p) / burstCount) *
                (rng.range(0, CONFIG.STAR.PARTICLE_BURST_SPEED_VAR) +
                  CONFIG.STAR.PARTICLE_BURST_SPEED_MIN),
              CONFIG.STAR.PARTICLE_LIFE,
              CONFIG.STAR.PARTICLE_LIFE,
              rng.range(0, CONFIG.STAR.PARTICLE_SIZE_VARIATION) + CONFIG.STAR.PARTICLE_SIZE_MIN,
              (function () {
                const isRed = star && star.isRed;
                if (!isRed) return CONFIG.COLORS.STAR.BASE;
                const palette = BackgroundManager.getCurrentNebulaPalette();
                if (palette === "blue" && CONFIG.COLORS.STAR_BLUE)
                  return CONFIG.COLORS.STAR_BLUE.BASE;
                return CONFIG.COLORS.STAR_RED.BASE;
              })()
            )
          );
        }
      })
    );

    return () => {
      for (const off of unsubs) off();
    };
  },
};
